import { z } from "zod";
import type { EventType, IngestEvent } from "./wire.js";

/**
 * Ingest keys. The api worker creates a key, shows it once, and writes
 * `KEYS[await hashIngestKey(key)] = JSON.stringify(IngestKeyRecord)`. The ingest worker reads it.
 * Only the hash is stored, so a KV dump never reveals a usable key.
 */
/**
 * "server" (default): a secret key for backends, every ingest path. "client" (additive): a publishable key
 * that ships inside apps on people's devices (iOS, Android, Flutter, browser extensions). Anyone can read
 * it out of the app, so ingest only takes it on POST /v1/events, only for error/custom/metric events, never
 * lets it wake anyone directly, and rate-limits it per device instead of per key. No key, of either kind,
 * works on the admin API: ingest keys are write-only.
 */
export const IngestKeyKind = z.enum(["server", "client"]);
export type IngestKeyKind = z.infer<typeof IngestKeyKind>;

/** Raw client keys start with this, so people (and scrubbers) can tell them from secret `pl_live_` keys. */
export const CLIENT_KEY_PREFIX = "pl_pub_";

/** Event types a client key may send. Heartbeats, deploys, resolves and check-ins steer alerting: server only. */
export const CLIENT_KEY_EVENT_TYPES: readonly EventType[] = ["error", "custom", "metric"] as const;

export const IngestKeyRecord = z.object({
  accountId: z.string().min(1),
  projectId: z.string().min(1),
  revoked: z.boolean(),
  /** Absent on older records: those are server keys. */
  kind: IngestKeyKind.optional(),
  /** The `key_…` id, for audit and rotation. Optional so older records stay valid. */
  keyId: z.string().optional(),
  /**
   * Signing secrets for alert webhooks, by HookSource (Stripe whsec_…, Sentry client secret, or one
   * you chose). Optional: older records and keys without saved secrets stay valid.
   */
  hookSecrets: z.record(z.string().max(32), z.string().min(1).max(256)).optional(),
});
export type IngestKeyRecord = z.infer<typeof IngestKeyRecord>;

/** KV key for an ingest key: lowercase hex SHA-256 of the raw key (UTF-8). Uses Web Crypto only. */
export async function hashIngestKey(rawKey: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(rawKey));
  let hex = "";
  for (const b of new Uint8Array(digest)) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/** The key's kind; records written before kinds existed are server keys. */
export const keyKind = (record: Pick<IngestKeyRecord, "kind">): IngestKeyKind => record.kind ?? "server";

/**
 * What a client key may send, as a pure check: a refusal reason for event types it may not send, else the
 * event to accept. A custom event asking to wake someone is accepted as "tell": the decider can still
 * raise it, but a key anyone can copy out of an app must not page a person by itself.
 */
export function clientKeyEvent(event: IngestEvent): { ok: true; event: IngestEvent } | { ok: false; reason: string } {
  if (!CLIENT_KEY_EVENT_TYPES.includes(event.type)) {
    return {
      ok: false,
      reason: `A client key can't send "${event.type}" events. Send it from your server with a server key.`,
    };
  }
  if (event.severity === "wake") return { ok: true, event: { ...event, severity: "tell" } };
  return { ok: true, event };
}

/** POST /v1/projects/:id/keys body (optional). No body: a server key, as before. */
export const CreateIngestKeyBody = z.object({ kind: IngestKeyKind.default("server") });
export type CreateIngestKeyBody = z.infer<typeof CreateIngestKeyBody>;
